import { createContext, useContext } from 'react';
import type { PlayerId, ProfileId, SkillBand } from '../../shared/types/ids';
import type { SessionLaunchConfig } from '../../shared/types/session-launch';
import type { ThriveRepositories } from '../../storage/interfaces/repositories';
import type { FamilyErasureResult } from '../../storage/local/family-erasure';
import type { StorageBackend } from '../../storage/local/key-value-store';
import type { TunablesProvider } from '../../platform/config/tunables-provider';
import type { NarrationService } from '../../sdk/narration';
import type { AudioService } from '../../platform/audio';
import type { CameraSession } from '../../interaction/camera';

/**
 * One wiring point for the whole shell. Repositories, narration and tunables are constructed
 * once by the provider and read from here — no component builds its own, and nothing reaches
 * for browser storage directly (architecture §18, §25.6).
 */

/** Who is about to play. A guest keeps no persistent progress. */
export interface ActivePlayer {
  playerId: PlayerId;
  label: string;
  band: SkillBand;
  isGuest: boolean;
  profileId?: ProfileId;
  /**
   * **Which generation of that profile was chosen** (P6R17-1, D-056(6)).
   *
   * Profile ids are caller-supplied and `saveChild` upserts on them, so an id alone cannot tell a
   * child from a child created under the same id after the first was deleted. `createdAt` is the
   * generation, and it is captured **here, at selection** — the moment a grown-up says who is
   * playing — because that is the only moment at which the answer is "who was chosen" rather than
   * "who is stored under this id right now". A sitting that re-read it later could adopt a
   * replacement child as its own.
   *
   * Session-scoped like the rest of this record: nothing here is persisted, so absence means
   * "choose again", never a guessed generation.
   */
  profileCreatedAt?: string;
}

export interface AppServices {
  repositories: ThriveRepositories;
  /**
   * A parent's delete, as physical erasure with a verification read-back (BL-003, PS6-3).
   *
   * An **operation**, never the key/value store it needs: a screen that could reach the store
   * could write to it, and PRO-01 exists so that no screen can. The parent zone is the only
   * caller, and what it is handed is the answer to "is it actually gone", not a handle.
   */
  eraseEverything(): Promise<FamilyErasureResult>;
  /**
   * Live storage status. Anything other than `local-storage` means this session's progress
   * will not survive a reload.
   */
  storageBackend: StorageBackend;
  narration: NarrationService & { readonly available: boolean };
  /**
   * The one sound owner for this sitting (PS5-2, D-094(3)).
   *
   * Beside the camera and narration for the same reason both are here: it holds a browser resource
   * that must be released on the way out, it has to be reachable from grown-up surfaces as well as
   * from the stage, and exactly one of it may exist. It starts **locked** — a browser's autoplay
   * policy is satisfied by the grown-up's existing start gesture and by nothing else, so nothing
   * can make a sound until a family has actually asked for a mission to begin.
   */
  audio: AudioService;
  tunables: TunablesProvider;
  /**
   * The one camera owner for this sitting (architecture §23, D-060(3)).
   *
   * It lives here rather than inside the readiness screen for two reasons: the pose engine costs
   * ~7.3 s to warm and must survive a child leaving the screen, and the camera-active indicator in
   * the shell has to be able to tell the truth from anywhere in the app. Session-scoped by
   * construction — it is an object, not a stored record, and nothing about it is persisted.
   */
  camera: CameraSession;
  /**
   * Whether this sitting answers with the camera (Slice D).
   *
   * Session-scoped React state and nothing else — the same rule `sessionLaunchConfig` follows and
   * for the same reason: how a child is answering tonight is a property of the evening, not a fact
   * about them. A reload therefore comes back at the keyboard, which is exactly right, because the
   * calibration it would need is session-only too (GDS §13) and is gone with it. **No storage key**
   * (recon storage watch, D-031/D-059).
   *
   * It is switched on where a child has just proved the camera can see them — the end of hero
   * training — and it is only ever a *request*: the stage still refuses to use the camera for a
   * child it has no calibration for. Parent Setup owns this choice from Slice F (D-059).
   */
  cameraInputEnabled: boolean;
  setCameraInputEnabled: (enabled: boolean) => void;
  activePlayer: ActivePlayer | null;
  setActivePlayer: (player: ActivePlayer | null) => void;
  /**
   * What the parent chose on the setup screen for this sitting.
   *
   * Session-scoped and never persisted (see `SessionLaunchConfig`): a deep link or a refresh
   * finds it null, and the stage recomputes the same defaults the setup screen would have shown.
   */
  sessionLaunchConfig: SessionLaunchConfig | null;
  setSessionLaunchConfig: (config: SessionLaunchConfig | null) => void;
}

export const AppServicesContext = createContext<AppServices | null>(null);

export function useAppServices(): AppServices {
  const services = useContext(AppServicesContext);
  if (services === null) {
    throw new Error('useAppServices must be used inside <AppServicesProvider>.');
  }
  return services;
}

export const GUEST_PLAYER: ActivePlayer = {
  playerId: 'guest-hero-1',
  label: 'Guest hero',
  band: 'explorer',
  isGuest: true,
};
